// Settings > Onboarding > Check installation. One rendered fetch of a brand
// page (default: the brand's website) answers both install questions: is
// the tag (gt.js) on the page under this brand, and is the Shopify pixel
// installed / reporting. The API only checks the brand's own domains or a
// *.myshopify.com store, once a minute (desirelist-api
// lib/controllers/onboardingCheck).
import React, { useState } from 'react'
import { Button, CircularProgress, TextField } from '@mui/material'
import toast from 'react-hot-toast'
import axiosInstant from '../../../../api'
import { brandOnboardingCheckEndpoint } from '../../../../api/endpoints'

interface CheckResult {
  url: string
  checked_at: string
  scrape_error: string | null
  // Only what this check saw — never "reported recently" — so a removal
  // shows on the next check. found null = the page could not be loaded.
  gt: { found: boolean | null; bids: number[]; bid_matches: boolean | null }
  shopify: null | {
    generated: boolean
    checked: boolean
    is_shopify: boolean
    custom_pixel_count: number
    token_found: boolean
    // The pixel reported while the check loaded the store.
    live: boolean
    live_at: string | null
  }
  // Consent tooling found on the page (null: the page could not be loaded).
  consent?: null | { signals: { key: string; name: string; standard: boolean }[] }
}

type Tone = 'ok' | 'warn' | 'bad' | 'muted'

const TONE_STYLE: Record<Tone, string> = {
  ok: 'bg-[#E8F5E9] text-[#2E7D32]',
  warn: 'bg-tint text-accent-text',
  bad: 'bg-[#FBEFEF] text-[#c62828]',
  muted: 'bg-tint text-text-2',
}

const ERROR_COPY: Record<string, string> = {
  UrlNotBrandOwned: 'That page is not on one of your domains. Check a page on your own site (or your myshopify.com store).',
  NoUrlToCheck: 'Enter a page URL to check — your brand has no website on file.',
  CheckRateLimited: 'A check just ran. Give it a minute before checking again.',
  RequestTimeout: 'The check took too long. Please try again — the next attempt is usually faster.',
}

const errorCode = (e: any): string => {
  const data = e?.response?.data
  if (typeof data === 'string') return data
  return data?.error || ''
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '')

function gtRow(r: CheckResult): { tone: Tone; label: string; detail: string } {
  if (r.gt.found === null) {
    return { tone: 'muted', label: "Couldn't check", detail: 'We could not load the page. Try again, or check another page.' }
  }
  if (r.gt.found && r.gt.bid_matches === false) {
    return {
      tone: 'bad',
      label: 'Wrong brand id',
      detail: `The tag on this page reports under brand ${r.gt.bids.join(', ')}. Replace it with the code below.`,
    }
  }
  if (r.gt.found) return { tone: 'ok', label: 'Tag installed', detail: 'Found on the page.' }
  return { tone: 'bad', label: 'Not found', detail: 'The tag is not on this page. Add it using the code below.' }
}

function pixelRow(r: CheckResult): { tone: Tone; label: string; detail: string } | null {
  const s = r.shopify
  if (!s) return null
  if (!s.generated) {
    return { tone: 'muted', label: 'Not set up', detail: 'Generate the pixel below, then add it in Shopify.' }
  }
  if (!s.checked) {
    return { tone: 'muted', label: "Couldn't check", detail: 'We could not load the page. Try again, or check another page.' }
  }
  if (s.live) {
    return { tone: 'ok', label: 'Pixel installed', detail: 'The pixel reported while we loaded your store.' }
  }
  if (s.token_found) {
    return { tone: 'ok', label: 'Pixel installed', detail: 'Your pixel is on the store; events will show once shoppers visit.' }
  }
  if (!s.is_shopify) {
    return { tone: 'muted', label: 'Not a Shopify store', detail: 'This page does not look like a Shopify storefront.' }
  }
  if (s.custom_pixel_count > 0) {
    return {
      tone: 'bad',
      label: 'Not detected',
      detail: `The store has ${s.custom_pixel_count} custom pixel${
        s.custom_pixel_count === 1 ? '' : 's'
      }, but ours did not report while we loaded it. Check it is added and connected in Shopify.`,
    }
  }
  return { tone: 'bad', label: 'Not found', detail: 'No custom pixel on this store. Add it using the steps below.' }
}

function consentRow(r: CheckResult): { tone: Tone; label: string; detail: string } | null {
  if (!r.consent) return null
  const found = r.consent.signals || []
  if (!found.length) {
    return {
      tone: 'muted',
      label: 'No banner found',
      detail: "No cookie banner on this page — the tag uses your Privacy & consent setting below.",
    }
  }
  return {
    tone: 'ok',
    label: 'Banner found',
    detail: `Found ${found.map((f) => f.name).join(', ')}. The tag follows the shopper's choice there.`,
  }
}

function ResultRow({ title, row }: { title: string; row: { tone: Tone; label: string; detail: string } }) {
  return (
    <div className="flex flex-wrap items-start gap-2">
      <span className="text-[13px] font-semibold text-text-1 w-[140px] shrink-0">{title}</span>
      <span className={`text-[11px] font-bold uppercase rounded-full px-2 py-0.5 ${TONE_STYLE[row.tone]}`}>
        {row.label}
      </span>
      <span className="text-[12px] text-text-2 min-w-0 basis-full sm:basis-auto sm:flex-1">{row.detail}</span>
    </div>
  )
}

export default function InstallCheck({ brandId, onChecked }: { brandId: number; onChecked?: () => void }) {
  const [url, setUrl] = useState('')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<CheckResult | null>(null)

  const run = async () => {
    setRunning(true)
    try {
      const { data } = await axiosInstant.post(brandOnboardingCheckEndpoint(brandId), url.trim() ? { url: url.trim() } : {})
      setResult(data)
      if (onChecked) onChecked()
    } catch (e: any) {
      const code = errorCode(e)
      toast.error(ERROR_COPY[code] || "Couldn't run the check. Please try again.")
    } finally {
      setRunning(false)
    }
  }

  const pixel = result ? pixelRow(result) : null

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-text-2 max-w-[720px]">
        Load a page of your store and confirm what’s installed. Leave the URL empty to check your website’s home
        page, or enter a product page.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <TextField
          size="small"
          label="Page URL (optional)"
          placeholder="https://yourstore.com/products/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !running) run()
          }}
          disabled={running}
          sx={{ minWidth: 320, flex: '1 1 320px', maxWidth: 520 }}
        />
        <Button variant="contained" disableElevation onClick={run} disabled={running}>
          {running ? (
            <>
              <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> Checking…
            </>
          ) : (
            'Check installation'
          )}
        </Button>
      </div>
      {running ? <p className="text-[12px] text-muted">Loading your page like a shopper would — this can take up to a minute.</p> : null}
      {result && !running ? (
        <div className="border border-border-token rounded-xl p-4 bg-surface flex flex-col gap-3">
          <p className="text-[12px] text-text-2 break-all">
            Checked <strong>{result.url}</strong> at {when(result.checked_at)}
          </p>
          <ResultRow title="Site tag (gt.js)" row={gtRow(result)} />
          {pixel ? <ResultRow title="Shopify pixel" row={pixel} /> : null}
          {consentRow(result) ? <ResultRow title="Cookie consent" row={consentRow(result) as any} /> : null}
        </div>
      ) : null}
    </div>
  )
}
